import {
    ENTITY_CLASSES,
    type EntityKind,
    type Normalized,
    PROJECTIONS,
} from './entityKinds';

/**
 * The field that marks an object as an entity, holding its kind. It is part of
 * normalized data at runtime, but not of its types.
 */
export const KIND_KEY = '__kind';

/** The key the normalizer identifies an entity by. */
export function entityKey(kind: EntityKind, id: string): string {
    return `${kind}:${id}`;
}

/**
 * The normalizer key of an object in normalized data, or `undefined` when it
 * isn't an entity.
 */
export function getEntityKey(
    value: Readonly<Record<string, unknown>>,
): string | undefined {
    const kind = value[KIND_KEY];
    if (kind === undefined) {
        return undefined;
    }
    const id = value.Id;
    if (typeof kind !== 'string' || typeof id !== 'string') {
        throw new Error(`Entity of kind ${JSON.stringify(kind)} has no string Id`);
    }
    return entityKey(kind as EntityKind, id);
}

/**
 * Converts SDK data (e.g. a response) to normalized data: SDK class instances
 * become plain objects at every depth, and objects representing an entity are
 * marked with their kind. `Date`s are kept as they are.
 *
 * Entities can only be recognized from SDK class instances, so pass data as
 * the SDK returns it.
 */
export function toNormalized<T>(value: T): Normalized<T> {
    return normalizeValue(value) as Normalized<T>;
}

function normalizeValue(value: unknown): unknown {
    if (typeof value !== 'object' || value === null || value instanceof Date) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(normalizeValue);
    }
    if (!isPlainObject(value) && !isSdkObject(value)) {
        throw new Error(`Can't normalize a ${value.constructor.name}`);
    }
    const rule = findKindRule(value);
    const normalized: Record<string, unknown> = {};
    for (const [key, field] of Object.entries(value)) {
        defineField(normalized, rule?.renames?.[key] ?? key, normalizeValue(field));
    }
    if (rule) {
        normalized[KIND_KEY] = rule.kind;
    }
    return normalized;
}

/**
 * Sets `target[key]` as an own data property. Unlike assignment, a `__proto__`
 * key is kept as a field rather than replacing `target`'s prototype.
 */
export function defineField(
    target: Record<string, unknown>,
    key: string,
    value: unknown,
): void {
    Object.defineProperty(target, key, {
        value,
        writable: true,
        enumerable: true,
        configurable: true,
    });
}

type KindRule = Readonly<{
    kind: EntityKind;
    renames?: Readonly<Record<string, string | undefined>>;
}>;

/** The entity kind `value` represents, with its field renames, if any. */
function findKindRule(value: object): KindRule | undefined {
    const projected = PROJECTIONS.find((p) => value instanceof p.sdkClass);
    if (projected) {
        return projected;
    }
    const kind = ENTITY_KINDS.find((k) =>
        ENTITY_CLASSES[k].some((sdkClass) => value instanceof sdkClass),
    );
    return kind && { kind };
}

const ENTITY_KINDS = Object.keys(ENTITY_CLASSES) as EntityKind[];

function isPlainObject(value: object): boolean {
    const prototype: unknown = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/** Whether `value` is an SDK class instance, which keep their data in own fields. */
function isSdkObject(value: object): boolean {
    return (
        'toJSON' in value &&
        typeof value.toJSON === 'function' &&
        'init' in value &&
        typeof value.init === 'function'
    );
}
